import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/** @jsxRuntime classic */
/** @jsx jsx */
/** @jsxfrag */
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { css, jsx } from '@emotion/react';
import PropTypes from 'prop-types';
import { useAppProvisioning } from '@atlassian/app-provisioning';
import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics';
import { AppRecommendation } from '../app-recommendation';
import { getExperimentCohort, NUMBER_OF_RECOMMENDED_APPS, TBLZ_SWITCHER_ROLE_BASED_EXPERIMENT_NAME } from './experiment';
import { createLoomExperiment } from './loom-demand-experiment/create-loom-experiment';
var UndoDismissFlag = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: "@atlaskit-internal_app-recommendation-flag" */'../app-recommendation-flag');
});
var ListResetStyles = css({
  padding: 0,
  margin: 0
});
var containerStyles = css({
  listStyleType: 'none',
  margin: 0
});
export var AppRecommendationsList = function AppRecommendationsList(_ref) {
  var _context$locale;
  var actionBus = _ref.actionBus,
    flagNotInterestedSubtitle = _ref.flagNotInterestedSubtitle,
    flagNotInterestedTitle = _ref.flagNotInterestedTitle,
    apps = _ref.apps,
    mutate = _ref.mutate,
    context = _ref.context,
    recommendationReasonsModalAcceptButton = _ref.recommendationReasonsModalAcceptButton,
    recommendationReasonsModalExplanation = _ref.recommendationReasonsModalExplanation,
    recommendationReasonsModalPrivacyPolicyLinkHref = _ref.recommendationReasonsModalPrivacyPolicyLinkHref,
    recommendationReasonsModalPrivacyPolicyLinkText = _ref.recommendationReasonsModalPrivacyPolicyLinkText,
    recommendationReasonsModalTitle = _ref.recommendationReasonsModalTitle,
    adControlsMenuLabel = _ref.adControlsMenuLabel,
    adControlsMenuNotInterested = _ref.adControlsMenuNotInterested,
    adControlsMenuWhyAmISeeingThis = _ref.adControlsMenuWhyAmISeeingThis;
  var appProvisioning = useAppProvisioning();
  var _useFireCrossFlowAnal = useFireCrossFlowAnalyticsEvents(),
    fireViewedUIEvent = _useFireCrossFlowAnal.fireViewedUIEvent;

  // TBLZ-494: Switcher - Updated Recommendations and Targeting
  // go/trailblazerexperiments
  var tblzSwitcherRoleBasedExperimentCohort = getExperimentCohort(context);
  var loomExperiment = createLoomExperiment(fireViewedUIEvent);
  if ((_context$locale = context.locale) !== null && _context$locale !== void 0 && _context$locale.startsWith('en') && tblzSwitcherRoleBasedExperimentCohort !== 'not-enrolled' && apps.length > 0) {
    fireViewedUIEvent({
      // Using @ts-ignore here to avoid the strict typing in `surface-analytics`.
      // `surface-analytics` doesn't have track events, so using a UI event to send exposure event.
      // @ts-ignore
      actionSubject: 'feature',
      // @ts-ignore
      action: 'exposed',
      // @ts-ignore
      actionSubjectId: TBLZ_SWITCHER_ROLE_BASED_EXPERIMENT_NAME
    }, {
      source: 'atlassianSwitcherDiscoverProductsSection',
      recommendedProductIds: apps.map(function (app) {
        return app.productId;
      })
    }, {
      flagKey: TBLZ_SWITCHER_ROLE_BASED_EXPERIMENT_NAME,
      cohort: tblzSwitcherRoleBasedExperimentCohort
    });
  }
  if (tblzSwitcherRoleBasedExperimentCohort === 'experiment' && apps.length > NUMBER_OF_RECOMMENDED_APPS) {
    apps.splice(NUMBER_OF_RECOMMENDED_APPS);
  }
  // TBLZ-494

  // stores the list of dismissed apps
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    dismissedApps = _useState2[0],
    setDismissedApps = _useState2[1];

  // stores the order/arrangement of all apps
  var _useState3 = useState([apps]),
    _useState4 = _slicedToArray(_useState3, 2),
    _ = _useState4[0],
    setStateRegister = _useState4[1];
  var removeFlag = function removeFlag(id) {
    setDismissedApps(function (current) {
      return current.filter(function (flag) {
        return flag.productId !== id;
      });
    });
  };
  useEffect(function () {
    var unsubscribe = actionBus === null || actionBus === void 0 ? void 0 : actionBus.on('DISMISS_APP', function (_ref2) {
      var productId = _ref2.productId;
      loomExperiment.captureNotInterestedDismissal(productId);
      var app = apps.find(function (searchApp) {
        return searchApp.productId === productId;
      });
      if (app) {
        setDismissedApps([app].concat(_toConsumableArray(dismissedApps)));
        setStateRegister(function (listOfPrevStates) {
          return [].concat(_toConsumableArray(listOfPrevStates), [apps]);
        });
      }
    });
    return function () {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [apps, actionBus, dismissedApps, loomExperiment]);
  var dispatchSurfaceViewedEvent = useCallback(function () {
    actionBus === null || actionBus === void 0 || actionBus.dispatch('USER_VIEW_DISCOVERY_SURFACE', {
      action: {
        action: 'viewed',
        actionSubject: 'section',
        actionSubjectId: 'atlassianSwitcherDiscoverProducts'
      },
      source: 'atlassianSwitcher'
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(function () {
    dispatchSurfaceViewedEvent();
  }, [dispatchSurfaceViewedEvent]);
  useEffect(function () {
    if (apps.length === 0) {
      actionBus === null || actionBus === void 0 || actionBus.dispatch('USE_EMPTY_APPS_TITLE');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apps.length]);
  var appRecommendationMessages = {
    recommendationReasonsModalAcceptButton: recommendationReasonsModalAcceptButton,
    recommendationReasonsModalExplanation: recommendationReasonsModalExplanation,
    recommendationReasonsModalPrivacyPolicyLinkHref: recommendationReasonsModalPrivacyPolicyLinkHref,
    recommendationReasonsModalPrivacyPolicyLinkText: recommendationReasonsModalPrivacyPolicyLinkText,
    recommendationReasonsModalTitle: recommendationReasonsModalTitle
  };
  apps = loomExperiment.isEnabled ? loomExperiment.addLoomToSwitcherRecommendations(apps) : apps;
  useEffect(function () {
    loomExperiment.fireExposureEvent(apps);
    // we only want to fire the exposure once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return jsx("ul", {
    css: ListResetStyles,
    "data-testid": 'discovery-sdk-component--app-recommendations-list'
  }, apps.map(function (app, index) {
    return jsx("li", {
      css: containerStyles,
      key: index
    }, jsx(AppRecommendation, _extends({}, app, appRecommendationMessages, {
      actionBus: actionBus,
      context: context,
      mutate: mutate,
      journey: app.journey,
      appProvisioning: appProvisioning,
      key: (app.productId, index),
      adControlsMenuLabel: adControlsMenuLabel,
      adControlsMenuNotInterested: adControlsMenuNotInterested,
      adControlsMenuWhyAmISeeingThis: adControlsMenuWhyAmISeeingThis
    })));
  }), jsx(Suspense, {
    fallback: null
  }, jsx(UndoDismissFlag, {
    title: flagNotInterestedTitle,
    description: flagNotInterestedSubtitle,
    flags: dismissedApps.map(function (app) {
      return _objectSpread(_objectSpread({}, app), appRecommendationMessages);
    }),
    removeFlag: removeFlag,
    closeDismissedFlag: function closeDismissedFlag(productId) {
      actionBus === null || actionBus === void 0 || actionBus.dispatch('CLOSE_APP_DISMISSED_FLAG', {
        productId: productId,
        action: {
          action: 'clicked',
          actionSubject: 'button',
          actionSubjectId: 'adControlsFlagClose'
        },
        source: 'atlassianSwitcherDiscoverProductsSection'
      });
    }
  })));
};
AppRecommendationsList.propTypes = {
  apps: PropTypes.arrayOf(PropTypes.shape({
    productId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    logo: PropTypes.string.isRequired,
    journey: PropTypes.oneOf(['get-started', 'decide', 'discover']).isRequired,
    reasons: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired
  }).isRequired).isRequired,
  recommendationReasonsModalExplanation: PropTypes.string.isRequired,
  recommendationReasonsModalPrivacyPolicyLinkText: PropTypes.string.isRequired,
  recommendationReasonsModalPrivacyPolicyLinkHref: PropTypes.string.isRequired,
  recommendationReasonsModalAcceptButton: PropTypes.string.isRequired,
  adControlsMenuLabel: PropTypes.string.isRequired,
  adControlsMenuNotInterested: PropTypes.string.isRequired,
  adControlsMenuWhyAmISeeingThis: PropTypes.string.isRequired,
  flagNotInterestedTitle: PropTypes.string.isRequired,
  flagNotInterestedSubtitle: PropTypes.string.isRequired
};